import React, { useEffect, useState, useRef } from 'react'

// next components
import Head from 'next/head'
import Image from 'next/image'

// solana
import { Connection, PublicKey } from '@solana/web3.js'
import type { OriginalEditionAccountData, PrintEditionAccountData } from '@metaplex-foundation/js'
import {
  Metaplex,
  toOriginalEditionAccount,
  toOriginalOrPrintEditionAccount,
  toNftOriginalEdition,
} from '@metaplex-foundation/js'
import { Key } from '@metaplex-foundation/mpl-token-metadata'
import { useWallet } from '@solana/wallet-adapter-react'

// components
import PrimaryLayout from 'components/Layout/PrimaryLayout'
import MainSection from 'components/pages/BrowseNFT/MainSection'
import MerchSection from 'components/pages/BrowseNFT/MerchSection'
import MoreCollectionSection from 'components/pages/BrowseNFT/MoreCollectionSection'
import MoreArtistSection from 'components/pages/BrowseNFT/MoreArtistSection'
import MoreCategorySection from 'components/pages/BrowseNFT/MoreCategorySection'
import ShopApparelSection from 'components/pages/BrowseNFT/ShopApparelSection'
import SelectedTab from 'components/Tab/SelectedTab'

// custom functions
import { getNFTCategories, getNftFromDAS, getNftFromRpc, NFTArtistInfo } from 'utils/nfts.utils'

// images and icons
import CloseIcon from 'assets/icons/close-screen.svg'

// third party components
import { useDispatch, useSelector } from 'react-redux'

// styles
import styles from './index.module.scss'

// resources
import DefaultNftBG from 'assets/images/default-nft-bg.png'
import ShopApparelList from 'assets/json/browse-nft/shop-apparel-list.json'
import TabList from 'assets/json/browse-nft/auction-list.json'
import MerchTabList from 'assets/json/browse-nft/merch-list.json'

import nftsAPI from 'apis/nfts'
import activityAPI from 'apis/activity'
import { isString, compareDate, optimizeCdnUrlOgImage } from 'utils/helpers'
import { getAbsoluteOgUrl } from 'utils/seo'
import FullScreenLoadingModal from 'components/Modal/FullScreenLoadingModal'
import { useAuthentication } from 'hooks/useAuthentication'
import ProvenanceSection from 'components/pages/BrowseNFT/ProvenanceSection'
import ModelViewer from 'components/ModelViewer'
import { NftData, NftApiData } from 'types/nft'
import { useViewport } from 'utils'
import NFTActivityInfoSection from 'components/pages/BrowseNFT/MainSection/NFTActivityInfoSection'
import collectionAPI from 'apis/collections'
import dashboardAPI from 'apis/dashboard'

export interface EditionDetail {
  supply: number
  maxSupply?: number
  print?: {
    number: number
  }
  isOriginal: boolean
  isSingleNft: boolean
  isOpenEdition: boolean
  isLimitedEdition: boolean
}

export interface BrowseNFTProps {
  nftDetail?: NftData
  editionDetail?: EditionDetail
  mintAddress?: PublicKey
  error?: string
}

const BrowseNFT = ({ nftDetail, editionDetail }: BrowseNFTProps) => {
  const viewport = useViewport()
  const nftType = (nftDetail?.category as string) ?? 'image'
  const isLoading = useSelector<any>(state => state.isLoading)
  const apparelType = useSelector<any>(state => state.apparelType)
  const detailShow = useSelector<any>(state => state.detailShow)
  const [categories, setCategories] = useState(getNFTCategories(nftDetail))
  const [openMerchPage, setOpenMerchPage] = useState(false)
  const [merchData, setMerchData] = useState(null)
  const [nftInfo, setNftInfo] = useState<NftData>()
  const [collectionName, setCollectionName] = useState('')
  const [artistInfo, setArtistInfo] = useState<NFTArtistInfo | null>(null)
  const { connected } = useWallet()
  const { profileInfo } = useAuthentication()
  const dispatch = useDispatch()
  const [showLoading, setShowLoading] = useState(true)
  const [showAuctionSection, setShowAuctionSection] = useState(false)
  const [merchToggle, setMerchToggle] = useState(false)
  const [activeTab, setActiveTab] = useState<any>(0)
  const [tabList, setTabList] = useState<any[]>(TabList)
  const [activityList, setActivityList] = useState([])

  useEffect(() => {
    setTimeout(
      () =>
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        }),
      500
    )
    dispatch({ type: 'set', isLoading: false })
  }, [])

  useEffect(() => {
    if (isLoading === false) setShowLoading(false)
  }, [isLoading])

  useEffect(() => {
    if (detailShow === true) {
      if (apparelType !== undefined || apparelType !== '' || apparelType !== null) {
        const nftName = nftDetail.name ?? 'No name found'
        const nftCoverUrl = nftDetail.image ?? ''
        const mintAddress = nftDetail.mintAddress.toString()
        const item = ShopApparelList.find(apparel => apparel.name === apparelType)
        let params = {
          nftName,
          nftCoverUrl,
          mintAddress,
          category: item?.category,
          subCategory: item?.sub_type,
          base: item?.base,
        }
        setMerchData(params)
        setOpenMerchPage(true)
        dispatch({ type: 'set', detailShow: false })
      }
    }
  }, [detailShow, apparelType])

  useEffect(() => {
    const fetchData = async () => {
      if (!nftDetail) return
      if (connected) {
        await nftsAPI.getDetail(nftDetail.mintAddress).then(
          (response: any) => {
            if (!isString(response)) {
              setNftInfo(response)
              if (response.creator_address !== null) {
                setArtistInfo({ address: response.creator_address, name: response.creator_username })
              }
            }
          },
          error => {}
        )
      } else {
        await nftsAPI.get(nftDetail.mintAddress).then(
          (response: any) => {
            if (!isString(response)) {
              setNftInfo(response)
              if (response.creator_address !== null) {
                setArtistInfo({ address: response.creator_address, name: response.creator_username })
              }
            }
          },
          error => {}
        )
      }

      activityAPI.getNFTActivity(nftDetail.mintAddress).then(
        response => {
          let dateSorted = response.sort(compareDate)
          setActivityList(dateSorted)
        },
        error => {}
      )
    }
    fetchData()
  }, [nftDetail, profileInfo])

  const collectionId = nftDetail?.collection?.address?.toString()

  let nftName = nftDetail?.name ?? ''
  if (editionDetail) {
    let { print, supply, maxSupply } = editionDetail
    let supplyInfo = `#${print?.number}/${supply}`
    if (supply !== maxSupply) supplyInfo = `edition ${print?.number}/${supply}, max ${maxSupply ?? '\u221e'}`
    nftName = `${nftName} (${supplyInfo})`
  }
  let nftTitle = `${nftName} | Foster Marketplace`
  let nftImageUrl = optimizeCdnUrlOgImage(getAbsoluteOgUrl(nftDetail?.image || DefaultNftBG.src))

  let nftDescription = nftDetail?.description ?? ''

  const [nftCoverUrl, setNftCoverUrl] = useState('')
  const [fullScreen, setFullScreen] = useState(false)
  const setFullScreenView = url => {
    window.scrollTo({
      top: 0,
    })
    document.body.style.overflow = 'hidden'
    setFullScreen(true)
    setNftCoverUrl(url)
  }

  const reloadNftData = async () => {
    if (!nftDetail) return
    if (connected) {
      await nftsAPI.getDetail(nftDetail.mintAddress).then(
        (response: any) => {
          if (!isString(response)) {
            setNftInfo(response)
            if (response.creator_address !== null) {
              setArtistInfo({ address: response.creator_address, name: response.creator_username })
            }
          }
        },
        error => {}
      )
    } else {
      await nftsAPI.get(nftDetail.mintAddress).then(
        (response: any) => {
          if (!isString(response)) {
            setNftInfo(response)
            if (response.creator_address !== null) {
              setArtistInfo({ address: response.creator_address, name: response.creator_username })
            }
          }
        },
        error => {}
      )
    }
  }

  const handleSelectedTab = item => {
    setActiveTab(item.id)
  }

  useEffect(() => {
    let array: any[] = []
    let list
    if (merchToggle) list = MerchTabList
    else list = TabList
    list.map(item => {
      let object: any = item
      if (item.id === activeTab) {
        object.selected = true
      } else {
        object.selected = false
      }
      array.push(object)
    })
    setTabList(array)
  }, [activeTab, merchToggle])

  // more collection nfts
  const [collectionNFTs, setCollectionNFTs] = useState([])
  useEffect(() => {
    if (collectionId !== null) {
      collectionAPI.getCollectionNft(collectionId).then(
        response => {
          if (response instanceof Array) {
            let resData = response.filter(item => item.token_id !== collectionId)
            if (resData.length === 0) return
            if (viewport === 'xs' || viewport === 'sm' || viewport === 'md') {
              setCollectionNFTs(resData.filter(item => item.cover_image_url !== null))
            } else {
              setCollectionNFTs(resData)
            }
          }
        },
        error => {}
      )
    }
  }, [collectionId])

  // artist collections
  const [collectionList, setCollectionList] = useState([])
  useEffect(() => {
    if (artistInfo !== null && artistInfo.address) {
      collectionAPI.getOtherCollections(artistInfo.address).then(
        response => {
          if (response instanceof Array) {
            setCollectionList(response)
          }
        },
        error => {}
      )
    }
  }, [artistInfo])

  // category collection list
  const [categoryCollectionList, setCategoryCollectionList] = useState([])

  useEffect(() => {
    if (categories !== null && categories !== undefined && categories[0] !== undefined) {
      dashboardAPI.getTrendingCollections(categories[0], 'week').then(
        response => {
          if (response instanceof Array) {
            setCategoryCollectionList(response)
          }
        },
        error => {}
      )
    }
  }, [categories])

  useEffect(() => {
    if (categoryCollectionList.length > 0 || collectionList.length > 0 || collectionNFTs.length > 0) {
      setTabList(prev => {
        if (prev.some(t => t.label === 'Discover More')) {
          return prev
        } else {
          return [...prev, { id: 1, label: 'Discover More', value: 'Discover More', selected: false }]
        }
      })
    } else {
      setTabList(prev => prev.filter(t => t.label !== 'Discover More'))
    }
  }, [categoryCollectionList, collectionList, collectionNFTs, connected])

  return (
    <div className="w-full h-full">
      <Head>
        <title>{nftTitle}</title>
        <meta property="description" content={nftDescription} />

        <meta name="og:title" content={nftTitle} />
        <meta property="og:description" content={nftDescription} />
        {nftType && <meta name="og:type" content={nftType} />}
        <meta name="og:image" content={nftImageUrl} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={nftTitle} />
        <meta property="twitter:description" content={nftDescription} />
        <meta property="twitter:image" content={nftImageUrl} />

        <link rel="icon" href="/logo.png" />
      </Head>
      <div className={styles.container}>
        {openMerchPage ? (
          <div className="flex justify-center">
            <div className="g-container z-10">
              <MerchSection
                data={merchData}
                openNftPage={() => {
                  setOpenMerchPage(false)
                }}
              />
            </div>
          </div>
        ) : (
          <div className="z-10">
            {nftDetail && (
              <div className={styles.mainBG}>
                <div className="g-container">
                  <MainSection
                    nftDetail={nftDetail}
                    editionDetail={editionDetail}
                    nftInfo={nftInfo}
                    setFullScreenView={setFullScreenView}
                    reloadNftData={reloadNftData}
                    enableMerch={setMerchToggle}
                    setCollectionName={setCollectionName}
                    fullScreen={fullScreen}
                    setShowAuctionSection={enable => setShowAuctionSection(enable)}
                  />
                </div>
              </div>
            )}

            {!showAuctionSection && (
              <div className={`g-container ${styles.activityContainer} mt-[30px] lg:mt-[20px]`}>
                <div className={styles.selectedTab}>
                  <SelectedTab tabList={tabList} onChange={item => handleSelectedTab(item)} />
                  <div className={styles.selectedTabBorder} />
                </div>
                <div className={styles.mainContainer}>
                  {activeTab === 0 ? (
                    <NFTActivityInfoSection activityList={activityList} />
                  ) : activeTab === 1 ? (
                    <div>
                      {collectionId && collectionNFTs && (
                        <MoreCollectionSection
                          collectionNFTs={collectionNFTs}
                          collectionId={collectionId}
                          collectionName={collectionName}
                          mintAddress={nftDetail.mintAddress.toString()}
                        />
                      )}
                      {artistInfo && collectionList && (
                        <MoreArtistSection artistInfo={artistInfo} collectionList={collectionList} />
                      )}
                      <MoreCategorySection categories={categories} categoryCollectionList={categoryCollectionList} />
                    </div>
                  ) : (
                    merchToggle && (
                      <ShopApparelSection
                        title="Shop Apparel"
                        type="apparel"
                        nftDetail={nftDetail}
                        shopApparelList={ShopApparelList}
                        openMerchPage={data => {
                          setMerchData(data)
                          setOpenMerchPage(true)
                        }}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {fullScreen && (
        <div className={styles.fullScreen}>
          {nftType?.includes('vr') ? (
            <ModelViewer path={nftCoverUrl} fullScreen width={innerWidth} height={innerHeight} />
          ) : (
            <img
              src={nftCoverUrl}
              onError={() => {
                setNftCoverUrl(nftDetail?.original?.image)
              }}
              alt=""
              className="w-full h-full max-w-full max-h-full object-contain"
            />
          )}
          <div
            className={styles.closeScreen}
            onClick={() => {
              setFullScreen(false)
              document.body.style.overflow = 'unset'
            }}
          >
            <Image src={CloseIcon} alt="" width={18} height={18} />
          </div>
        </div>
      )}

      <FullScreenLoadingModal open={showLoading} />
    </div>
  )
}

export default BrowseNFT

BrowseNFT.getLayout = function getLayout(page) {
  return <PrimaryLayout>{page}</PrimaryLayout>
}

BrowseNFT.getInitialProps = async function ({ res, query }): Promise<BrowseNFTProps> {
  // set cache headers
  if (res) {
    // cache for 30m, shared cache for 1h
    res.setHeader('cache-control', 'max-age=1800, s-maxage=3600')
  }

  let mintAddress: PublicKey
  try {
    mintAddress = new PublicKey(query.id)
  } catch (e: any) {
    console.error(e)
    return {
      error: `could not parse as public key: ${query.id}: ${e.message}`,
    }
  }

  let nftDetail

  try {
    const metaplex = Metaplex.make(new Connection(process.env.PROVIDER_URL))

    let nftDetail = (await getNftFromDAS(metaplex, query.id)) ?? (await getNftFromRpc(metaplex, query.id))

    let editionDetail: EditionDetail | undefined = undefined
    try {
      const { data: nftEdition } = await metaplex
        .rpc()
        .getAccount(metaplex.nfts().pdas().masterEdition({ mint: mintAddress }))
        .then(toOriginalOrPrintEditionAccount)

      let originalEdition: OriginalEditionAccountData
      let print: PrintEditionAccountData | null = null

      if ('parent' in nftEdition) {
        print = nftEdition
        ;({ data: originalEdition } = await metaplex.rpc().getAccount(nftEdition.parent).then(toOriginalEditionAccount))
      } else originalEdition = nftEdition
      editionDetail = {
        print: print?.edition && { number: Number(print.edition) },
        supply: Number(originalEdition.supply),
        maxSupply: originalEdition.maxSupply && Number(originalEdition.maxSupply),
        isOriginal: ![Key.MasterEditionV1, Key.MasterEditionV2].includes(originalEdition.key),
        isSingleNft: originalEdition.maxSupply === 0,
        isOpenEdition: originalEdition.maxSupply === null,
        isLimitedEdition: Number(originalEdition.maxSupply) > 0,
      }
    } catch (error) {
      console.error(error)
    }

    return {
      nftDetail,
      editionDetail,
      mintAddress,
    }
  } catch (e: any) {
    console.error(e)
    return {
      error: `could not load nft: ${query.id}: ${e.message}`,
    }
  }
}
